import React from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

import { AttendeeStatus } from "./utils";
import AttendeeChip from "../chips/attendee-chip";
import { TeamSummary } from "../../types.new";

interface SelectedUserTagItem {
  userId: string;
  label: string;
  teams: TeamSummary[];
  status?: AttendeeStatus;
}

interface AttendeeListProps {
  selectedUsers: SelectedUserTagItem[];
  selectedInviteEmails: string[];
  selectedTeams: TeamSummary[];
  editable?: boolean;
  lockedUserIds?: string[];
  lockedInviteEmails?: string[];
  lockedTeamIds?: string[];
  onRemoveUser?: (userId: string) => void;
  onRemoveInviteEmail?: (email: string) => void;
  onRemoveTeam?: (teamId: string) => void;
  split?: boolean;
}

const AttendeeList: React.FC<AttendeeListProps> = ({
  selectedUsers,
  selectedInviteEmails,
  selectedTeams,
  editable = false,
  lockedUserIds = [],
  lockedInviteEmails = [],
  lockedTeamIds = [],
  onRemoveUser,
  onRemoveInviteEmail,
  onRemoveTeam,
  split = false,
}) => {
  const hasUsers = selectedUsers.length > 0 || selectedInviteEmails.length > 0;
  const hasTeams = selectedTeams.length > 0;

  const renderUserTags = (fullWidth = false) => (
    <Stack
      direction={fullWidth ? "column" : "row"}
      spacing={0}
      rowGap={0.75}
      columnGap={0.75}
      flexWrap={fullWidth ? undefined : "wrap"}
      sx={fullWidth ? { width: "100%" } : undefined}
    >
      {selectedUsers.map(({ userId, label, teams, status }) => {
        const isLocked = lockedUserIds.includes(userId);

        return (
          <Box
            key={`selected-user-${userId}`}
            sx={
              fullWidth
                ? { width: "100%", "& .MuiChip-root": { width: "100%" } }
                : undefined
            }
          >
            <AttendeeChip
              attendee={{
                type: "user",
                userId,
                label,
                teams,
                status,
              }}
              editable={editable && !isLocked}
              onDelete={
                editable && !isLocked ? () => onRemoveUser?.(userId) : undefined
              }
              fullWidth={fullWidth}
            />
          </Box>
        );
      })}

      {selectedInviteEmails.map((inviteEmail) => {
        const isLocked = lockedInviteEmails.includes(inviteEmail);

        return (
          <Box
            key={`selected-email-${inviteEmail}`}
            sx={
              fullWidth
                ? { width: "100%", "& .MuiChip-root": { width: "100%" } }
                : undefined
            }
          >
            <AttendeeChip
              attendee={{
                type: "invite",
                email: inviteEmail,
              }}
              editable={editable && !isLocked}
              onDelete={
                editable && !isLocked
                  ? () => onRemoveInviteEmail?.(inviteEmail)
                  : undefined
              }
              fullWidth={fullWidth}
            />
          </Box>
        );
      })}
    </Stack>
  );

  const renderTeamTags = (fullWidth = false) => (
    <Stack
      direction={fullWidth ? "column" : "row"}
      spacing={0}
      rowGap={0.75}
      columnGap={0.75}
      flexWrap={fullWidth ? undefined : "wrap"}
      sx={fullWidth ? { width: "100%" } : undefined}
    >
      {selectedTeams.map((team) => {
        const isLocked = lockedTeamIds.includes(
          team.teamId,
        );

        return (
          <Box
            key={`selected-org-${team.teamId}`}
            sx={
              fullWidth
                ? { width: "100%", "& .MuiChip-root": { width: "100%" } }
                : undefined
            }
          >
            <AttendeeChip
              attendee={{
                type: "team",
                teamId: team.teamId,
                name: team.name,
                color: team.color,
              }}
              editable={editable && !isLocked}
              onDelete={
                editable && !isLocked
                  ? () => onRemoveTeam?.(team.teamId)
                  : undefined
              }
              fullWidth={fullWidth}
            />
          </Box>
        );
      })}
    </Stack>
  );

  if (split) {
    return (
      <Stack direction="row" spacing={1} width="100%">
        <Stack
          direction="column"
          spacing={0.75}
          sx={{
            flex: 1,
            minWidth: 0,
            border: "1px solid #d0d7de",
            borderRadius: 2,
            p: 1,
            backgroundColor: "#fff",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: "text.secondary",
              px: 0.5,
              fontSize: "0.8rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <PersonOutlineIcon sx={{ fontSize: "0.8rem" }} />
            Users
          </Typography>
          <Divider flexItem />
          {hasUsers ? (
            renderUserTags(true)
          ) : (
            <Box
              sx={{
                backgroundColor: "#f3f4f6",
                borderRadius: 1.5,
                py: 0.75,
                px: 1.25,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.75rem",
                  textAlign: "center",
                  display: "block",
                }}
              >
                No users
              </Typography>
            </Box>
          )}
        </Stack>

        {/* <Stack
                    direction="column"
                    spacing={0.75}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        border: "1px solid #d0d7de",
                        borderRadius: 2,
                        p: 1,
                        backgroundColor: "#fff",
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 500,
                            color: "text.secondary",
                            px: 0.5,
                            fontSize: "0.8rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                        }}
                    >
                        <BusinessOutlinedIcon sx={{ fontSize: "0.8rem" }} />
                        Teams
                    </Typography>
                    <Divider flexItem />
                    {hasTeams ? renderTeamTags(true) : (
                        <Box
                            sx={{
                                backgroundColor: "#f3f4f6",
                                borderRadius: 1.5,
                                py: 0.75,
                                px: 1.25,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "text.secondary",
                                    fontSize: "0.75rem",
                                    textAlign: "center",
                                    display: "block",
                                }}
                            >
                                No teams
                            </Typography>
                        </Box>
                    )}
                </Stack> */}
      </Stack>
    );
  }
  return (
    <Stack
      direction="row"
      spacing={0}
      rowGap={0.75}
      columnGap={0.75}
      flexWrap="wrap"
      sx={{ width: "100%" }}
    >
      {renderUserTags()}
      {renderTeamTags()}
    </Stack>
  );
};

export default AttendeeList;
